// WowPos (비연동 POS) API 헬퍼
// OKDC/Union 와 동일 패턴 — 손님 페이지 (orderPage, cartDrawer, staffCallBottomSheet) 에서 사용

import { authHeaders } from './api';
import wsService from './websocket';

const API_BASE_URL = process.env.REACT_APP_API_URL
  || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000');

// 멱등키 생성 — 소켓·HTTP 폴백 양쪽에 동일하게 실어 이중 경로여도 서버가 1건만 처리(중복 방지)
function genUuid() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch (_) { /* noop */ }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

/**
 * 타임아웃 + 1회 재시도 fetch.
 * iOS Safari 가 죽은 keep-alive 커넥션을 재사용하면 요청이 TCP 타임아웃(수십 초)까지 stall 되는데,
 * 타임아웃으로 끊고 재시도하면 Safari 가 새 커넥션을 열어 즉시 성공한다.
 * 정상망은 밀리초 응답이라 타임아웃(4초)에 절대 안 걸린다.
 * @param {string} url
 * @param {RequestInit} opts
 * @param {{ timeoutMs?: number, retries?: number }} cfg
 * @returns {Promise<Response>}
 */
async function fetchWithTimeout(url, opts = {}, { timeoutMs = 4000, retries = 1 } = {}) {
  let lastErr;
  for (let attempt = 0; attempt <= retries; attempt++) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const res = await fetch(url, { ...opts, signal: controller.signal });
      clearTimeout(timer);
      return res;
    } catch (err) {
      clearTimeout(timer);
      lastErr = err;
      // 마지막 시도가 아니면 재시도(새 커넥션으로 stale 소켓 우회)
      if (attempt < retries) continue;
      // 사용자가 상황을 알 수 있게 명확한 에러
      const isTimeout = err && err.name === 'AbortError';
      const e = new Error(isTimeout ? '네트워크 응답이 지연됩니다. 다시 시도해주세요.' : (err.message || '네트워크 오류'));
      e.cause = err;
      e.isTimeout = isTimeout;
      throw e;
    }
  }
  throw lastErr;
}

/**
 * 주문 전송
 * @param {Object} payload  { storeId, tableCode, items, totalAmount }
 * @returns {Promise<{success:boolean, error?:string}>}
 */
export async function sendOrderToWowpos(payload) {
  // 멱등키 1개를 소켓·HTTP 양쪽에 동일하게 실음 → 이중 경로여도 서버가 1건만 반영
  const body = { ...payload, clientUuid: payload.clientUuid || genUuid() };

  // 1) 소켓 우선(업그레이드된 살아있는 WS 일 때만) — 손님 Safari 의 죽은 keep-alive HTTP stall 우회
  if (wsService.isWebsocketUpgraded && wsService.isWebsocketUpgraded()) {
    try {
      const resp = await wsService.emitWithAck('wowpos-submit-order', body, { timeoutMs: 2000 });
      if (resp && typeof resp.success !== 'undefined') return resp;
      // 응답 형식 이상 → HTTP 폴백
    } catch (_) {
      // ack 타임아웃/무응답(좀비 소켓 등) → HTTP 폴백 (동일 clientUuid 라 중복 안전)
    }
  }

  // 2) HTTP 폴백 — 기존 경로(타임아웃 4초 + 1회 재시도). 서버 dedup + 멱등키로 중복 안전.
  const res = await fetchWithTimeout(`${API_BASE_URL}/api/wowpos/orders`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return res.json();
}

/**
 * 직원 호출
 * @param {Object} payload  { storeId, tableName, reasonIds }
 */
export async function sendStaffCallToWowpos(payload) {
  const res = await fetch(`${API_BASE_URL}/api/wowpos/staff-call`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return res.json();
}

/**
 * 메뉴 데이터 조회 (손님 페이지가 직접 fetch 시 사용)
 * 통합 응답 (기존 /api/store/:storeId/menus 와 동일 구조)
 */
export async function fetchWowposMenuData(storeId) {
  const res = await fetch(`${API_BASE_URL}/api/wowpos/menu-data/${storeId}`);
  return res.json();
}

/**
 * 테이블별 누적 주문내역 조회.
 * 와우포스는 주문 데이터의 마스터가 매장 PC (서버 미저장) 이므로
 * 백엔드가 WS 로 매장 PC 에 질의 → 결과 반환.
 *
 * @param {number|string} storeId
 * @param {string} tableCode
 * @returns {Promise<{success:boolean, orders:Array, warning?:string}>}
 *   orders 항목: { tableCode, table_name, is_new_order, total_price, items:[{name,qty,options:[...]}], receivedAt }
 */
export async function fetchWowposOrderHistory(storeId, tableCode) {
  // 1) 소켓 우선 — 리뷰 페이지 전환을 막는 조회라 stall 시 UI가 멈춤. read 라 폴백도 안전.
  if (wsService.isWebsocketUpgraded && wsService.isWebsocketUpgraded()) {
    try {
      const resp = await wsService.emitWithAck('wowpos-history-check', { storeId, tableCode }, { timeoutMs: 2000 });
      if (resp && typeof resp.success !== 'undefined') return resp;
    } catch (_) {
      // ack 타임아웃/무응답 → HTTP 폴백
    }
  }

  // 2) HTTP 폴백 (타임아웃 4초 + 재시도)
  const res = await fetchWithTimeout(
    `${API_BASE_URL}/api/wowpos/table/order/history/${storeId}/${encodeURIComponent(tableCode)}`
  );
  return res.json();
}

/**
 * 테이블 초기화 — 관리자에서 직접 (POS PC 안 만지고) 해당 테이블 주문 초기화.
 * 백엔드가 wowpos_orders status open→cleared + 전 PC ws(table-clear) 브로드캐스트.
 * @param {number|string} storeId
 * @param {string} tableCode
 * @returns {Promise<{success:boolean, error?:string}>}
 */
export async function resetWowposTable(storeId, tableCode) {
  const res = await fetch(`${API_BASE_URL}/api/wowpos/table/reset`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ storeId, tableCode }),
  });
  return res.json();
}

/**
 * 주문 취소 — 테이블 초기화 + 해당 세션 매출(woworder_sales) 영구 삭제.
 * 관리자 주문내역 상세 우측상단 "주문 취소" 전용. 매출 삭제라 인증 필요(authHeaders).
 * @param {number|string} storeId  숫자 stores.id
 * @param {string} tableCode
 * @returns {Promise<{success:boolean, error?:string}>}
 */
export async function cancelWowposTable(storeId, tableCode) {
  const res = await fetch(`${API_BASE_URL}/api/wowpos/table/cancel`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ storeId, tableCode }),
  });
  return res.json();
}

/**
 * 관리자 앱 알림 재유도 — 진입/포그라운드 시 미처리 주문(open) + 최근 호출/리뷰완료 조회.
 * @param {number|string} storeId
 * @returns {Promise<{success:boolean, orders:Array, calls:Array, reviews:Array}>}
 */
export async function fetchWowposAppNotifications(storeId) {
  const res = await fetch(`${API_BASE_URL}/api/wowpos/app-notifications/${storeId}`, { headers: authHeaders() });
  return res.json();
}

/**
 * 호출 처리 — 관리자 앱에서 호출 팝업을 닫으면 해당 테이블 미처리 호출을 resolved 표기.
 * 재진입 시 다시 뜨지 않게 함.
 * @param {number|string} storeId
 * @param {string} tableCode
 */
export async function resolveWowposCall(storeId, batchId) {
  const res = await fetch(`${API_BASE_URL}/api/wowpos/staff-call/resolve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ storeId, batchId }),
  });
  return res.json();
}

/**
 * 주문 알림 dismiss — 관리자 앱에서 주문 팝업을 닫으면 해당 주문 이벤트를 처리 표기.
 * 각 제출(새주문/추가주문)이 별도 이벤트라 개별로 사라지고 재진입 시 합쳐지지 않음.
 * @param {number|string} orderEventId
 */
export async function dismissWowposOrderEvent(orderEventId) {
  const res = await fetch(`${API_BASE_URL}/api/wowpos/order-event/${orderEventId}/dismiss`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
  });
  return res.json();
}

/**
 * 리뷰완료 알림 dismiss — 해당 리뷰 이벤트를 처리 표기(재진입 시 제외).
 * @param {number|string} reviewEventId
 */
export async function dismissWowposReview(reviewEventId) {
  const res = await fetch(`${API_BASE_URL}/api/wowpos/review-event/${reviewEventId}/dismiss`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
  });
  return res.json();
}
